import bbox from "@turf/bbox";
import FeatureService from "mapbox-gl-arcgis-featureserver";

const maps = {},
    initBounds = [-127, 24, -67, 50];

export default function createMap(id, basemaps, overlays, icons) {
    const dataLayer = overlays.find((overlay) => overlay.dataLayer);

    let map;
    if (maps[id]) {
        map = maps[id];
        // Based on react-map-gl's reuse() logic
        const oldContainer = map.getContainer(),
            newContainer = document.getElementById(id);
        newContainer.className = oldContainer.className;
        while (oldContainer.childNodes.length > 0) {
            newContainer.appendChild(oldContainer.childNodes[0]);
        }
        map._container = newContainer;
        map.resize();
        map.getSource(dataLayer.name).setData(dataLayer.data);
        map.fitBounds(bbox(dataLayer.data), { padding: 48 });
    } else {
        // https://maplibre.org/maplibre-gl-js-docs/api/map/
        // (unpkg.com/maplibre-gl)
        map = new maplibregl.Map({
            container: id,
            style: basemaps.find((basemap) => basemap.active).data,
            bounds: initBounds,
            maxZoom: 9,
            maplibreLogo: true,
            dragRotate: false,
            touchZoomRotate: false,
            touchPitch: false,
            attributionControl: false,
        });

        const attributionControl = new maplibregl.AttributionControl({
            compact: true,
        });
        map.addControl(attributionControl);
        map.addControl(
            new maplibregl.NavigationControl({ showCompass: false }),
            "top-left"
        );
        map.addControl(new ResetBoundsControl(initBounds), "top-left");
        map.addControl(new maplibregl.GeolocateControl(), "top-left");
        map.addControl(
            new maplibregl.FullscreenControl({
                container: document.getElementById(
                    "block-views-block-mapping-block-1"
                ),
            })
        );
        map.addControl(
            new LayerControl({ basemaps, overlays, createOverlays }),
            "top-right"
        );

        maps[id] = map;
        map.on("load", createOverlays);

        const updateAttributions = attributionControl._updateAttributions;
        attributionControl._updateAttributions = function () {
            updateAttributions.call(this);
            this._updateCompactMinimize();
        };
    }

    async function createOverlays() {
        await Promise.all(Object.entries(icons).map(loadIcons));
        overlays.map(createOverlay);
    }

    // https://maplibre.org/maplibre-gl-js-docs/api/map/#map#addimage
    const pixelRatio = map.getPixelRatio();
    async function loadIcons([name, [url1x, url2x]]) {
        if (pixelRatio > 1) {
            await loadIcon(name, url2x, 2);
        } else {
            await loadIcon(name, url1x, 1);
        }
    }
    async function loadIcon(name, url, pixelRatio) {
        const data = await new Promise((resolve, reject) =>
            map.loadImage(url, (error, data) =>
                error ? reject(error) : resolve(data)
            )
        );
        map.addImage(name, data, { pixelRatio });
    }

    function createOverlay(conf) {
        const fn = overlayTypes[conf.type];
        if (!fn) {
            throw new Error(`Unknown layer type ${conf.type}`);
        }
        fn(conf, map);
        if (conf.onCreate) {
            conf.onCreate();
        }
    }
    return map;
}

const overlayTypes = {
    geojson(conf, map) {
        // https://maplibre.org/maplibre-gl-js-docs/api/map/#map#addsource
        map.addSource(conf.name, { type: "geojson", data: conf.data });
        for (const layer of conf.layers) {
            map.addLayer({
                ...layer,
                layout: {
                    ...layer.layout,
                    visibility: conf.active ? "visible" : "none",
                },
                source: conf.name,
            });
            if (conf.dataLayer) {
                // https://maplibre.org/maplibre-gl-js-docs/example/popup-on-click/
                map.on("click", layer.id, (evt) => {
                    const feature =
                        conf.data.features.find(
                            (f) => f.id == evt.features[0].id
                        ) || evt.features[0];
                    highlightProject(conf, map, feature);
                    evt.preventDefault();
                });
                map.on("click", (evt) => {
                    if (evt.defaultPrevented) {
                        return;
                    }
                    clearHighlight(conf, map);
                });
                map.on("mouseenter", layer.id, () => {
                    map.getCanvas().style.cursor = "pointer";
                });
                map.on("mouseleave", layer.id, () => {
                    map.getCanvas().style.cursor = "";
                });
            }
        }
    },
    vectorTile(conf, map) {
        if (conf.url) {
            map.addSource(conf.name, { type: "vector", tiles: [conf.url] });
        }
        for (const layer of conf.layers) {
            map.addLayer({
                source: conf.name,
                "source-layer": layer.id,
                ...layer,
                layout: {
                    ...layer.layout,
                    visibility: conf.active ? "visible" : "none",
                },
            });
        }
    },
    agolFeature(conf, map) {
        const service = new FeatureService(conf.name, map, {
            url: conf.url,
            token: conf.token,
        });
        for (const layer of conf.layers) {
            map.addLayer({
                ...layer,
                layout: {
                    ...layer.layout,
                    visibility: conf.active ? "visible" : "none",
                },
                source: conf.name,
            });
        }
    },
};

class ResetBoundsControl {
    constructor(bounds) {
        this._bounds = bounds;
    }
    onAdd(map) {
        this._map = map;
        const root = (this._container = document.createElement("div")),
            button = document.createElement("button"),
            span = document.createElement("span");
        root.appendChild(button);
        button.appendChild(span);

        root.className = "maplibregl-ctrl maplibregl-ctrl-group";
        button.className = "ctrl-reset-bounds";
        button.type = "button";
        span.className = "maplibregl-ctrl-icon";

        root.addEventListener("contextmenu", (evt) => evt.preventDefault());
        button.addEventListener("click", this.trigger.bind(this));

        return root;
    }
    trigger() {
        this._map.fitBounds(this._bounds);
    }
    onRemove() {
        const root = this._container;
        root.parentNode.removeChild(root);
    }
}

class LayerControl {
    constructor({ basemaps, overlays, createOverlays }) {
        this._basemaps = basemaps;
        this._overlays = overlays;
        this._createOverlays = createOverlays;
    }
    onAdd(map) {
        this._map = map;
        const root = (this._container = document.createElement("div")),
            panel = (this._panel = document.createElement("div")),
            basemapPanel = document.createElement("div"),
            basemapHeader = document.createElement("h2"),
            overlayPanel = document.createElement("div"),
            overlayHeader = document.createElement("h2"),
            button = (this._button = document.createElement("button")),
            span = document.createElement("span");

        root.appendChild(button);
        root.appendChild(panel);
        panel.appendChild(basemapPanel);
        panel.appendChild(overlayPanel);
        basemapPanel.append(basemapHeader);
        overlayPanel.append(overlayHeader);
        button.appendChild(span);

        root.className = "maplibregl-ctrl maplibregl-ctrl-group";
        panel.className = "switch-style-panel";
        basemapHeader.innerText = "Basemap";
        overlayHeader.innerText = "Overlays";
        button.className = "ctrl-switch-style";
        button.type = "button";
        span.className = "maplibregl-ctrl-icon";

        root.addEventListener("contextmenu", (evt) => evt.preventDefault());
        button.addEventListener("click", this.toggle.bind(this));

        for (const basemap of this._basemaps) {
            const bmButton = document.createElement("button"),
                bmIcon = document.createElement("span"),
                bmText = document.createElement("h4");

            basemapPanel.appendChild(bmButton);
            bmButton.appendChild(bmIcon);
            bmButton.appendChild(bmText);
            bmText.innerText = basemap.name;
            bmButton.className = "basemap-toggle";
            bmIcon.className = "icon";

            if (basemap.active) {
                bmButton.classList.add("active-style");
            }
            basemap.toggle = bmButton;

            bmButton.addEventListener("click", () => this.applyStyle(basemap));
        }

        for (const overlay of this._overlays) {
            if (!overlay.info) {
                continue;
            }
            const details = document.createElement("details"),
                summary = document.createElement("summary"),
                ovButton = document.createElement("button"),
                ovIcon = document.createElement("span"),
                ovText = document.createElement("h4");

            const info = document.createElement("div"),
                ref = document.createElement("div"),
                refButton = document.createElement("button"),
                refIcon = document.createElement("span"),
                refDetail = document.createElement("div"),
                refParagraph = document.createElement("p"),
                refLink = document.createElement("a"),
                desc = document.createElement("p"),
                form = document.createElement("form"),
                select = document.createElement("select"),
                legend = document.createElement("div");

            overlayPanel.insertBefore(details, overlayPanel.children[1]);
            details.appendChild(summary);
            details.appendChild(info);
            summary.appendChild(ovButton);
            summary.appendChild(ovText);
            ovButton.appendChild(ovIcon);
            info.appendChild(ref);
            if (overlay.choropleths) {
                info.appendChild(form);
                form.appendChild(select);
            }
            info.appendChild(legend);
            ref.appendChild(desc);
            ovText.innerText = overlay.name;
            details.className = "overlay-toggle";
            ovIcon.className = "icon";
            ref.className = "reference-row";
            info.className = "overlay-info";
            if (overlay.active) {
                details.classList.add("active-style");
            }
            desc.innerText = overlay.info.description || overlay.name;

            if (overlay.info.reference) {
                ref.appendChild(refButton);
                ref.append(refDetail);
                refButton.appendChild(refIcon);
                refDetail.append(refParagraph);
                refDetail.append(refLink);
                refButton.className = "reference-button";
                refIcon.className = "icon";
                refDetail.className = "reference-detail";
                refParagraph.innerText = overlay.info.reference;
                refLink.innerText = "More Information";
                refLink.href = overlay.info.reference_url;
                refLink.target = "_blank";
            }

            if (overlay.choropleths) {
                select.addEventListener("change", (evt) => {
                    const choropleth = overlay.choropleths.find(
                        (c) => c.name === evt.target.value
                    );
                    overlay.activeChoropleth = choropleth;
                    updateChoropleth();
                });
                for (const choropleth of overlay.choropleths) {
                    const option = document.createElement("option");
                    option.value = choropleth.name;
                    option.innerText = choropleth.label;
                    select.appendChild(option);
                }
            }

            if (overlay.info.legend) {
                createLegend(overlay.info.legend);
            } else if (overlay.choropleths) {
                overlay.activeChoropleth = overlay.choropleths[0];
                overlay.onCreate = updateChoropleth;
            }

            overlay.toggle = details;

            ovButton.addEventListener("click", () =>
                this.toggleOverlay(overlay)
            );
            details.addEventListener("toggle", () => {
                if (details.open && !overlay.active) {
                    this.toggleOverlay(overlay);
                }
            });
            refButton.addEventListener("click", () => {
                refDetail.classList.toggle("open");
            });

            function createLegend(legendItems) {
                legend.innerHTML = "";
                for (const [label, color] of Object.entries(legendItems)) {
                    const row = document.createElement("div"),
                        symbol = document.createElement("span"),
                        text = document.createElement("span");
                    row.appendChild(symbol);
                    row.appendChild(text);
                    legend.appendChild(row);
                    text.innerText = label;
                    row.className = "legend-item";
                    symbol.className = "legend-symbol";
                    symbol.style.backgroundColor = color;
                }
            }

            function updateChoropleth() {
                const choropleth = overlay.activeChoropleth,
                    legendItems = {},
                    layer = overlay.layers[0],
                    fallbackColor = layer.paint["fill-color"],
                    fillColor = ["match", ["get", choropleth.name]];
                for (const [name, style] of Object.entries(
                    choropleth.classes
                )) {
                    let color, label;
                    if (Array.isArray(style)) {
                        [color, label] = style;
                    } else {
                        color = style;
                        label = name;
                    }
                    legendItems[label] = color;
                    fillColor.push(name);
                    fillColor.push(color);
                }
                fillColor.push(fallbackColor);
                createLegend(legendItems);
                map.setPaintProperty(layer.id, "fill-color", fillColor);
            }
        }

        return root;
    }
    toggle() {
        if (this._expanded) {
            this.collapse();
        } else {
            this.expand();
        }
    }
    expand() {
        this._container.className =
            "maplibregl-ctrl maplibregl-ctrl-group expanded";
        this._expanded = true;
    }
    collapse() {
        this._container.className = "maplibregl-ctrl maplibregl-ctrl-group";
        this._expanded = false;
    }
    applyStyle(basemap) {
        const currentBasemap = this._basemaps.find((b) => b.active);
        if (basemap === currentBasemap) {
            return;
        }
        currentBasemap.active = false;
        basemap.active = true;

        const root = this._container;
        for (const basemap of this._basemaps) {
            if (basemap.active) {
                basemap.toggle.classList.add("active-style");
            } else {
                basemap.toggle.classList.remove("active-style");
            }
        }

        this._map.setStyle(basemap.data, { diff: false });
        this._map.once("idle", this._createOverlays);
    }
    toggleOverlay(overlay) {
        overlay.active = !overlay.active;
        for (const layer of overlay.layers) {
            this._map.setLayoutProperty(
                layer.id,
                "visibility",
                overlay.active ? "visible" : "none"
            );
        }
        if (overlay.active) {
            overlay.toggle.classList.add("active-style");
            if (overlay.mutex) {
                for (const otherOverlay of this._overlays) {
                    if (otherOverlay === overlay) {
                        continue;
                    }
                    if (otherOverlay.mutex && otherOverlay.active) {
                        this.toggleOverlay(otherOverlay);
                    }
                }
            }
        } else {
            overlay.toggle.classList.remove("active-style");
        }
    }
    onRemove() {
        const root = this._container;
        root.parentNode.removeChild(root);
    }
}

export function highlightProject(conf, map, feature) {
    clearHighlight(conf, map);

    map.getSource(`${conf.name}_highlight`).setData({
        type: "FeatureCollection",
        features: [feature],
    });

    if (feature["geometry-polygon"]) {
        map.getSource(`${conf.name}_polygon`).setData({
            type: "FeatureCollection",
            features: [{ ...feature, geometry: feature["geometry-polygon"] }],
        });
    }

    const card = document.querySelector(`.project-listing.id-${feature.id}`);
    if (!card) {
        return;
    }

    card.classList.add("highlighted");

    if (card.scrollIntoViewIfNeeded) {
        card.scrollIntoViewIfNeeded();
    } else {
        card.scrollIntoView();
    }
}

function clearHighlight(conf, map) {
    map.getSource(`${conf.name}_highlight`).setData({
        type: "FeatureCollection",
        features: [],
    });
    map.getSource(`${conf.name}_polygon`).setData({
        type: "FeatureCollection",
        features: [],
    });

    for (const card of document.querySelectorAll(
        ".project-listing.highlighted"
    )) {
        card.classList.remove("highlighted");
    }
}
